import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import { environment } from '../../environments/environment';
import { FunctionalityKey, LoginResponse, User } from './models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenKey = 'procurement_access_token';
  private readonly userKey = 'procurement_user';
  private readonly expiresAtKey = 'procurement_session_expires_at';
  private readonly userSubject = new BehaviorSubject<User | null>(this.loadUser());
  private sessionTimer: ReturnType<typeof setTimeout> | null = null;
  readonly user$ = this.userSubject.asObservable();

  constructor() {
    if (this.hasExpired()) {
      this.clearSession();
      return;
    }
    this.scheduleSessionTimeout();
  }

  login(username: string, password: string): Observable<LoginResponse> {
    const body = new URLSearchParams();
    body.set('username', username);
    body.set('password', password);
    return this.http.post<LoginResponse>(`${environment.apiBaseUrl}/auth/login`, body.toString(), {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    }).pipe(tap((response) => this.setSession(response)));
  }

  logout(): void {
    this.clearSession();
    this.router.navigateByUrl('/login');
  }

  private clearSession(): void {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    localStorage.removeItem(this.expiresAtKey);
    this.userSubject.next(null);
  }

  get token(): string | null {
    if (this.hasExpired()) {
      this.logout();
      return null;
    }
    return localStorage.getItem(this.tokenKey);
  }

  get currentUser(): User | null {
    return this.userSubject.value;
  }

  updateCurrentUser(user: User): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
    this.userSubject.next(user);
  }

  hasAccess(functionality: FunctionalityKey): boolean {
    const user = this.currentUser;
    if (!user) return false;
    if (user.role === 'admin' || user.functionality_access == null) return true;
    return user.functionality_access.includes(functionality);
  }

  get isAuthenticated(): boolean {
    if (this.hasExpired()) {
      this.logout();
      return false;
    }
    return !!this.token;
  }

  private setSession(response: LoginResponse): void {
    localStorage.setItem(this.tokenKey, response.access_token);
    localStorage.setItem(this.userKey, JSON.stringify(response.user));
    localStorage.setItem(this.expiresAtKey, String(Date.now() + response.expires_in * 1000));
    this.userSubject.next(response.user);
    this.scheduleSessionTimeout();
  }

  private loadUser(): User | null {
    if (this.hasExpired()) {
      this.clearStoredSession();
      return null;
    }
    const raw = localStorage.getItem(this.userKey);
    return raw ? JSON.parse(raw) as User : null;
  }

  private hasExpired(): boolean {
    const token = localStorage.getItem(this.tokenKey);
    if (!token) return false;
    const expiresAt = Number(localStorage.getItem(this.expiresAtKey) || 0);
    return expiresAt > 0 && Date.now() >= expiresAt;
  }

  private scheduleSessionTimeout(): void {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
    const expiresAt = Number(localStorage.getItem(this.expiresAtKey) || 0);
    const delay = expiresAt - Date.now();
    if (delay <= 0) return;
    this.sessionTimer = setTimeout(() => this.logout(), delay);
  }

  private clearStoredSession(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    localStorage.removeItem(this.expiresAtKey);
  }
}
